import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, fmtDate } from "@/components/PageHeader";
import { Link } from "react-router-dom";
import {
  Users2, GitPullRequestArrow, UserPlus, ListChecks, Route, Truck,
  Wrench, FileWarning, Image, ClipboardCheck, Loader2, Activity, MessageSquare, Star,
} from "lucide-react";

const CARD_MAP = {
  leads: { label: "Total Leads", icon: Users2, to: "/app/crm", tone: "text-brand" },
  new_inquiries: { label: "New Inquiries", icon: Users2, to: "/app/crm", tone: "text-primary" },
  unanswered: { label: "Unanswered", icon: MessageSquare, to: "/app/inbox", tone: "text-primary" },
  open_conversations: { label: "Open Conversations", icon: MessageSquare, to: "/app/inbox", tone: "text-brand" },
  new_reviews: { label: "New Reviews", icon: Star, to: "/app/reviews", tone: "text-ochre" },
  open_incidents: { label: "Open Incidents", icon: FileWarning, to: "/app/fleet", tone: "text-danger" },
  pending_referrals: { label: "Pending Referrals", icon: GitPullRequestArrow, to: "/app/referrals", tone: "text-ochre" },
  new_applicants: { label: "New Applicants", icon: UserPlus, to: "/app/applicants", tone: "text-terracotta" },
  open_followups: { label: "Open Follow-ups", icon: ListChecks, to: "/app/tasks", tone: "text-brand" },
  active_trips: { label: "Active Trips", icon: Route, to: "/app/tracking", tone: "text-success" },
  active_vehicles: { label: "Active Vehicles", icon: Truck, to: "/app/fleet", tone: "text-brand" },
  maintenance_alerts: { label: "Maintenance Alerts", icon: Wrench, to: "/app/maintenance", tone: "text-danger" },
  expiring_documents: { label: "Expiring Documents", icon: FileWarning, to: "/app/documents", tone: "text-terracotta" },
  total_media: { label: "Media Assets", icon: Image, to: "/app/media", tone: "text-brand" },
  media_review: { label: "Media In Review", icon: ClipboardCheck, to: "/app/media", tone: "text-ochre" },
  assigned_inquiries: { label: "Assigned Inquiries", icon: Users2, to: "/app/crm", tone: "text-brand" },
  assigned_referrals: { label: "Assigned Referrals", icon: GitPullRequestArrow, to: "/app/referrals", tone: "text-ochre" },
  my_tasks: { label: "My Tasks", icon: ListChecks, to: "/app/tasks", tone: "text-brand" },
};

function StatCard({ stat, value }) {
  const meta = CARD_MAP[stat] || { label: stat, icon: Activity, to: "/app", tone: "text-brand" };
  const Icon = meta.icon;
  return (
    <Link to={meta.to} data-testid={`stat-${stat}`}
      className="group border border-border rounded-lg bg-card p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{meta.label}</span>
        <Icon className={`w-[18px] h-[18px] ${meta.tone}`} />
      </div>
      <span className="font-heading text-3xl font-bold text-foreground group-hover:translate-x-0.5 transition-transform">{value}</span>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ stats: {} })); }, []);

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{data.role?.replace("_", " ")} Dashboard</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">{greet}, {user?.name?.split(" ")[0]}</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(data.stats || {}).map(([k, v]) => <StatCard key={k} stat={k} value={v} />)}
      </div>

      {data.role === "DRIVER" ? (
        <div className="border border-border rounded-lg bg-card p-6">
          <h2 className="font-heading text-lg font-semibold mb-1">Your day</h2>
          <p className="text-sm text-muted-foreground mb-4">Head to the Driver Workspace to start trips and inspections.</p>
          <Link to="/app/driver" className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="go-driver">
            <Route className="w-4 h-4" /> Open Driver Workspace
          </Link>
        </div>
      ) : (
        <div className="border border-border rounded-lg bg-card">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
            <Activity className="w-4 h-4 text-brand" />
            <h2 className="font-heading font-semibold">Recent Activity</h2>
          </div>
          <div className="divide-y divide-border">
            {(data.recent_activity || []).length === 0 && <div className="px-5 py-8 text-center text-muted-foreground text-sm">No activity yet.</div>}
            {(data.recent_activity || []).map((a) => (
              <div key={a.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <StatusBadge value={a.action} />
                <span className="text-foreground">{a.entity_type?.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground">by {a.user_name || "system"}</span>
                <span className="ml-auto text-muted-foreground text-xs">{fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
